import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Statistiques de l'assistant IA (chat client) par marchand.
//
// Chaque interaction produit/service du chat IA est journalisée dans
// `ai_chat_product_events` (affichage, clic, ajout au panier). Ces stats
// sont visibles dans le dashboard du marchand concerné ET dans celui du
// propriétaire de la marketplace (agrégation de tous ses vendeurs).
// ─────────────────────────────────────────────────────────────

export interface AiChatStatItem {
  name: string;
  display: number;
  click: number;
  cart: number;
  total: number;
}

export interface AiChatRecentEvent {
  id: number;
  eventType: string;
  productName: string | null;
  clientName: string | null;
  merchantId: number | null;
  query: string | null;
  createdAt: string;
}

export interface AiChatStats {
  display: number;
  click: number;
  cart: number;
  /** Nombre d'ouvertures de la fiche BOUTIQUE via l'IA. */
  boutiqueViews: number;
  total: number;
  uniqueClients: number;
  topItems: AiChatStatItem[];
  recent: AiChatRecentEvent[];
}

export const EMPTY_AI_CHAT_STATS: AiChatStats = {
  display: 0,
  click: 0,
  cart: 0,
  boutiqueViews: 0,
  total: 0,
  uniqueClients: 0,
  topItems: [],
  recent: [],
};

interface RawEvent {
  id: number;
  event_type: string | null;
  product_id: number | null;
  product_name: string | null;
  client_user_id: number | null;
  client_name: string | null;
  merchant_id: number | null;
  query: string | null;
  created_at: string | null;
}

function eventLabel(type: string): 'display' | 'click' | 'cart' | 'boutique' | 'other' {
  if (type === 'display' || type === 'click' || type === 'cart' || type === 'boutique') return type;
  return 'other';
}

/**
 * Récupère les statistiques du chat IA pour un ou plusieurs marchands.
 * `days` = 0 (ou omis) signifie « tout l'historique ».
 */
export async function fetchAiChatStats(
  merchantIds: number[],
  opts?: { days?: number; limit?: number },
): Promise<AiChatStats> {
  const ids = [...new Set((merchantIds || []).filter((n) => Number.isFinite(n) && n > 0))];
  if (ids.length === 0) return EMPTY_AI_CHAT_STATS;
  const days = opts?.days ?? 0;
  const limit = opts?.limit ?? 500;

  try {
    let query = supabase
      .from('ai_chat_product_events')
      .select('id, event_type, product_id, product_name, client_user_id, client_name, merchant_id, query, created_at')
      .in('merchant_id', ids)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (days > 0) {
      const since = new Date(Date.now() - days * 24 * 3600 * 1000).toISOString();
      query = query.gte('created_at', since);
    }
    const { data, error } = await query;
    if (error || !data) return EMPTY_AI_CHAT_STATS;
    const rows = data as RawEvent[];

    let display = 0;
    let click = 0;
    let cart = 0;
    let boutiqueViews = 0;
    const clients = new Set<number>();
    const byItem = new Map<string, AiChatStatItem>();

    rows.forEach((r) => {
      const kind = eventLabel(String(r.event_type || ''));
      if (kind === 'display') display += 1;
      else if (kind === 'click') click += 1;
      else if (kind === 'cart') cart += 1;
      else if (kind === 'boutique') boutiqueViews += 1;
      if (r.client_user_id != null) clients.add(Number(r.client_user_id));

      const key = r.product_name || (r.product_id != null ? `#${r.product_id}` : 'Contenu');
      const item = byItem.get(key) || { name: key, display: 0, click: 0, cart: 0, total: 0 };
      if (kind === 'display') item.display += 1;
      else if (kind === 'click') item.click += 1;
      else if (kind === 'cart') item.cart += 1;
      item.total += 1;
      byItem.set(key, item);
    });

    const topItems = [...byItem.values()]
      .sort((a, b) => b.total - a.total)
      .slice(0, 6);

    const recent: AiChatRecentEvent[] = rows.slice(0, 12).map((r) => ({
      id: Number(r.id),
      eventType: String(r.event_type || ''),
      productName: r.product_name,
      clientName: r.client_name,
      merchantId: r.merchant_id != null ? Number(r.merchant_id) : null,
      query: r.query,
      createdAt: r.created_at || '',
    }));

    return {
      display,
      click,
      cart,
      boutiqueViews,
      total: rows.length,
      uniqueClients: clients.size,
      topItems,
      recent,
    };
  } catch {
    return EMPTY_AI_CHAT_STATS;
  }
}